import { Box, RailzServiceNames } from 'vopay';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ConnectAccountingSoftware.module.scss';
import RailzConnect from './RailzConnect/RailzConnectFrame';
import ConnectSoftware from './ConnectSoftware/ConnectSoftware';
import ChartOfAccounts from './ChartOfAccounts/ChartOfAccounts';
import useFetchAspConnections from '#shared/components/AccountingSoftware/hooks/useFetchAspConnections';
import useFetchAspAccountAssignment from '#shared/components/AccountingSoftware/hooks/useFetchAspAccountAssignment';
import useFetchAspChartOfAccounts from '#shared/components/AccountingSoftware/hooks/useFetchAspChartOfAccounts';
import useAssignAspGLAccount from '#shared/components/AccountingSoftware/hooks/useAssignAspGLAccount';
import { useToast } from '#shared/providers/Toast';
import { useAlertDialog } from '#shared/providers/AlertDialog';

import { Skeleton } from '#shared/components/UI';

type ConnectAccountingSoftwareProps = {
  // Railz widget mount inputs — host facts the shared code can't read itself.
  railzWidgetId: string;
  // The VoPay AccountID the widget registers the business under.
  businessName: string;
  redirectURL: string;
  // Where the widget's close button lands. Defaults to redirectURL, which is wrong for a scoped
  // mount: a close that returns to a `?service=` URL just re-opens the widget, so the host must
  // pass an unscoped destination whenever it passes `service`.
  closeRedirectURL?: string;
  // Restricts the widget to a single accounting provider (an ERP card was clicked) and skips the
  // intro screen, since the provider has already been chosen. Omit to let the user pick.
  service?: RailzServiceNames;
  // Fired once both chart-of-account assignments persist. What happens next is
  // the host's business (the portal shows a success dialog and routes; the
  // embed reboots its lists).
  onCompleteChartOfAccount?: (chartOfAccountNameReceivable: string, chartOfAccountNamePayable: string) => void;
};

export default function ConnectAccountingSoftware({
  railzWidgetId,
  businessName,
  redirectURL,
  closeRedirectURL,
  service,
  onCompleteChartOfAccount,
}: ConnectAccountingSoftwareProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.AccountingSystem.AccountingSoftware.ChartOfAccount',
  });

  const toast = useToast();
  const alertDialog = useAlertDialog();

  const { connections, fetchAspConnections } = useFetchAspConnections();
  const { fetchAspAccountAssignment } = useFetchAspAccountAssignment();
  const { accounts, fetchAspChartOfAccounts } = useFetchAspChartOfAccounts();
  const { assigning, assignAspGLAccount } = useAssignAspGLAccount();

  const [currentChartReceivable, setCurrentChartReceivable] = useState('');
  const [currentChartPayable, setCurrentChartPayable] = useState('');
  const [originalChartReceivable, setOriginalChartReceivable] = useState('');
  const [originalChartPayables, setOriginalChartPayables] = useState('');
  const [isRailzConnect, setIsRailzConnect] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const [connection] = connections;

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await fetchAspConnections();
        const [activeConnection] = res.Connections;

        if (!activeConnection) {
          // Arrived from an ERP card: the provider is already chosen, so go straight to the widget
          if (service) setIsRailzConnect(true);

          return;
        }

        const [receivableAssignment, payableAssignment] = await Promise.all([
          fetchAspAccountAssignment({ ConnectionID: activeConnection.ConnectionID, Type: 'receivables' }),
          fetchAspAccountAssignment({ ConnectionID: activeConnection.ConnectionID, Type: 'payables' }),
          fetchAspChartOfAccounts({ ConnectionID: activeConnection.ConnectionID }),
        ]);

        setCurrentChartReceivable(receivableAssignment.Account?.AccountRefID || '');
        setOriginalChartReceivable(receivableAssignment.Account?.AccountRefID || '');
        setCurrentChartPayable(payableAssignment.Account?.AccountRefID || '');
        setOriginalChartPayables(payableAssignment.Account?.AccountRefID || '');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toast.neutral(
          t('loadError', {
            defaultValue: 'Could not load your accounting software settings: {{errorMessage}}',
            errorMessage,
          }),
        );
      } finally {
        setLoaded(true);
      }
    };

    loadData();
  }, []);

  const saveSelection = async () => {
    const selectedPayable = accounts.find((account) => account.AccountRefID === currentChartPayable);
    const selectedReceivable = accounts.find((account) => account.AccountRefID === currentChartReceivable);
    if (!connection || !selectedPayable || !selectedReceivable) return;

    // Sequential, not Promise.all: the endpoint read-modify-writes the whole
    // connection row, so concurrent assignments clobber each other.
    const assignments = [
      {
        type: 'receivables',
        sectionKey: 'sectionReceivables',
        sectionDefault: 'receivables',
        account: selectedReceivable,
        commit: setOriginalChartReceivable,
      },
      {
        type: 'payables',
        sectionKey: 'sectionPayables',
        sectionDefault: 'payables',
        account: selectedPayable,
        commit: setOriginalChartPayables,
      },
    ] as const;

    for (const { type, sectionKey, sectionDefault, account, commit } of assignments) {
      try {
        await assignAspGLAccount({
          ConnectionID: connection.ConnectionID,
          Type: type,
          AccountRefID: account.AccountRefID,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toast.neutral(
          t('updateError', {
            defaultValue: 'Could not update your {{section}} chart of accounts: {{errorMessage}}',
            section: t(sectionKey, { defaultValue: sectionDefault }),
            errorMessage,
          }),
        );

        return;
      }

      // Commit each assignment as it lands: if a later one fails, the earlier
      // one is already persisted server-side and must not still read as
      // unsaved in the form.
      commit(account.AccountRefID);
    }

    const receivableName = selectedReceivable.Name || selectedReceivable.AccountRefID;
    const payableName = selectedPayable.Name || selectedPayable.AccountRefID;
    onCompleteChartOfAccount?.(receivableName, payableName);
  };

  const handleSave = async () => {
    const isUpdate = originalChartPayables !== '' && originalChartReceivable !== '';

    if (isUpdate) {
      const result = await alertDialog.caution(
        t('Dialogs.ConfirmChanges.title', { defaultValue: 'Confirm Changes' }),
        t('Dialogs.ConfirmChanges.subTitle', {
          defaultValue:
            'Confirming this action will update your Accounting System details. Are you sure you would like to proceed?',
        }),
        {
          confirmLabel: t('Dialogs.ConfirmChanges.confirmButton', { defaultValue: 'Confirm' }),
          abortLabel: t('Dialogs.ConfirmChanges.cancelButton', { defaultValue: 'Cancel' }),
        },
      );

      if (result !== 'confirm') return;
    }

    await saveSelection();
  };

  return (
    <Box flx="row" jc="sa">
      <div className={styles.container}>
        <Box flx="col">
          {!loaded && <Skeleton height="320px" />}

          {loaded && connection && !isRailzConnect && (
            <ChartOfAccounts
              onSave={handleSave}
              isSaving={assigning}
              accounts={accounts}
              currentChartReceivable={currentChartReceivable}
              currentChartPayable={currentChartPayable}
              originalChartReceivable={originalChartReceivable}
              originalChartPayables={originalChartPayables}
              onReceivableChange={setCurrentChartReceivable}
              onPayableChange={setCurrentChartPayable}
              serviceName={connection.ServiceName ?? undefined}
            />
          )}

          {loaded && !connection && !isRailzConnect && (
            <ConnectSoftware
              onConnect={() => {
                setIsRailzConnect(true);
              }}
            />
          )}

          {isRailzConnect && (
            <RailzConnect
              redirectURL={redirectURL}
              closeRedirectURL={closeRedirectURL}
              widgetId={railzWidgetId}
              businessName={businessName}
              service={service}
            />
          )}
        </Box>
      </div>
    </Box>
  );
}
